import React, { createContext, useContext, useEffect, useState } from 'react'
import { AgTechTourConfig, AgTechTourStep } from './AgTechTourTypes'

type AgTechTourState = {
    isActive: boolean
    config: AgTechTourConfig | null
    currentStepIndex: number
}

export type AgTechTourContextValue = {
    isActive: boolean
    currentStepIndex: number
    config: AgTechTourConfig | null
    currentStep: AgTechTourStep | undefined
    startTour: (config: AgTechTourConfig) => void
    nextStep: () => void
    prevStep: () => void
    closeTour: () => void
}

const defaultValue: AgTechTourContextValue = {
    isActive: false,
    currentStepIndex: 0,
    config: null,
    currentStep: undefined,
    startTour: () => {},
    nextStep: () => {},
    prevStep: () => {},
    closeTour: () => {},
}

export const AgTechTourContext = createContext<AgTechTourContextValue>(defaultValue)

export const AgTechTourProvider: React.FC<React.PropsWithChildren<{ onClosePopups?: () => void }>> = ({ children, onClosePopups }) => {
    const [state, setState] = useState<AgTechTourState>({
        isActive: false,
        config: null,
        currentStepIndex: 0,
    })

    const currentStep = state.config?.steps[state.currentStepIndex]

    useEffect(() => {
        if (state.isActive && currentStep) {
            if (currentStep.closePopupsOnActivate) {
                onClosePopups?.()
            }
            currentStep.onActivate?.()
        }
    }, [state.currentStepIndex, state.isActive])

    const startTour = (config: AgTechTourConfig) => {
        setState({ isActive: true, config, currentStepIndex: 0 })
    }

    const nextStep = () => {
        setState(prev => {
            if (!prev.config) return prev
            const nextIndex = prev.currentStepIndex + 1
            if (nextIndex >= prev.config.steps.length) {
                return { isActive: false, config: null, currentStepIndex: 0 }
            }
            return { ...prev, currentStepIndex: nextIndex }
        })
    }

    const prevStep = () => {
        setState(prev => ({
            ...prev,
            currentStepIndex: Math.max(0, prev.currentStepIndex - 1)
        }))
    }

    const closeTour = () => {
        setState({ isActive: false, config: null, currentStepIndex: 0 })
    }

    return (
        <AgTechTourContext.Provider value={{ isActive: state.isActive, currentStepIndex: state.currentStepIndex, config: state.config, currentStep, startTour, nextStep, prevStep, closeTour }}>
            {children}
        </AgTechTourContext.Provider>
    )
}

export const useAgTechTour = () => useContext(AgTechTourContext)
